import { useCallback, useEffect, useRef } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { selectTableTabPageSize } from "../../User/redux/ui-preferences.selectors.ts"
import { patchListPageSize } from "../../User/redux/ui-preferences.thunks.ts"
import {
  LIST_PAGE_SIZE,
  LIST_PREFS_TABLE_KEY,
  LIST_PREFS_TAB_KEY,
  SEARCH_DEBOUNCE_MS,
  VALID_LIST_PAGE_SIZES
} from "../redux/global-map.constants"
import {
  selectActiveFilters,
  selectActiveLayers,
  selectListDirection,
  selectListEntity,
  selectListSort,
  selectListState,
  selectSearchTerm,
  setListPage,
  setListPageSize
} from "../redux/global-map.slice"
import { fetchListRows } from "../redux/global-map.thunks"
import type {
  EntityType,
  GlobalMapFilters,
  GlobalMapListResponse,
  ListEntityTab,
  ListSortField
} from "../redux/global-map.types"

interface UseListFeaturesResult {
  data: GlobalMapListResponse | null
  status: "idle" | "loading" | "refreshing" | "error"
  currentPage: number
  setPage: (page: number) => void
  pageSize: number
  setPageSize: (pageSize: number) => void
}

// `raw` is whatever `ui-preferences` happens to hold — possibly `null` (never
// saved), possibly a value from before the 5/10/20/50 constraint existed.
// Anything outside that set falls back to `LIST_PAGE_SIZE`, per this task's
// own spec.
const asValidPageSize = (raw: number | null): number | null =>
  raw !== null && (VALID_LIST_PAGE_SIZES as readonly number[]).includes(raw)
    ? raw
    : null

/**
 * Owns what keeps the List tab's rows in sync: the initial fetch on mount,
 * plus a fetch — resetting back to page 1 — on every change to the
 * committed filters, the free-text search (debounced, same as
 * `useMapFeatures`'s own search effect), the "Projects & Leads" layer
 * toggle, the List tab's own sub-tab (`listEntity`), sort field or sort
 * direction. A 7th trigger fetches whenever `currentPage` itself changes
 * (driven by `setPage` below, e.g. from `ListPaginationFooter`) — this one
 * does NOT reset back to page 1, since it moves within the current query.
 * An 8th trigger (Task 9) fetches whenever `pageSize` itself changes
 * (driven by `setPageSize`, from the same footer's size changer) — also no
 * page-1 reset of its own; `ListPaginationFooter`'s `onChange` is what
 * resets the page, by calling `onPageChange(1)` alongside
 * `onPageSizeChange`, so by the time this trigger runs `currentPage` has
 * already moved.
 *
 * Mirrors `useMapFeatures.ts` deliberately, including its central lesson:
 * each trigger below is guarded by a value-comparison ref
 * (`lastFetchedFiltersRef`-shaped), never a "skip the first run" boolean.
 * A boolean flag can only tell "is this the first time this effect has
 * run", which can't distinguish a mount-time change (already covered by the
 * mount effect above) from a genuine later one — this codebase shipped
 * exactly that bug once already (see `useMapFeatures.ts`'s own doc comment).
 *
 * `ready` (defaults `true`, matching `useMapFeatures`) exists for the same
 * reason it does there: a caller that gates the initial fetch on some other
 * mount-time read completing first (e.g. a future URL-sync hook for the
 * List tab) can pass `false` until that read lands, without this hook's
 * mount effect firing early against default/pre-read state.
 *
 * Page-number pagination (2026-09-20 backend contract change, replacing
 * cursor pagination): `currentPage` lives in `global-map.slice.ts`'s
 * `list.currentPage`, set via `setListPage`. Each of the six "start a new
 * query" triggers below pre-seeds `lastFetchedPageRef.current = 1` BEFORE
 * dispatching its own page-1 fetch — without that, the 7th (page-change)
 * effect would see `currentPage` change to 1 on the very next render (either
 * because the trigger dispatched `setListPage(1)` itself, for
 * filters/search/layers, or because the slice's own `setListEntity`/
 * `setListSort`/`toggleListDirection` reducers already reset it) and fire a
 * redundant second request for the exact page the triggering effect already
 * just fetched. The 7th and 8th triggers pre-seed EACH OTHER's ref the same
 * way, for the same reason: a page-size change from a page other than 1
 * changes both `currentPage` (reset to 1 by the footer) and `pageSize` in
 * the same commit, and only one of the two effects should actually fetch.
 *
 * Task 9 — a fresh mount no longer forces the session back to page 1.
 * `currentPage`/`pageSize` are read as-is from `global-map.slice.ts`'s
 * `list` state: either already resolved from the URL by
 * `useGlobalMapUrlSync`'s own mount-time read (its `page`/`pageSize` params
 * mirror its `tab`/`layers` ones exactly — see that hook's own doc comment),
 * which runs in `MapTab` before `ListTab` (and this hook) ever mounts, or —
 * absent a URL override — simply whatever this hook itself last left in
 * Redux from an earlier List-tab visit in the same session, since nothing
 * resets it on unmount either. This is what makes a Map→List→Map→List round
 * trip land back on the same page: `ListTab` unmounts whenever the user
 * switches to the Map tab (`MapTab.tsx` renders it conditionally), but
 * `currentPage` lives in Redux, not in this hook, so it survives the
 * unmount untouched.
 */
export function useListFeatures(
  ready: boolean = true,
  // Threaded from `MapTab` → `ListTab` (default `null` so every existing
  // direct call site/test that doesn't know about the URL keeps working
  // unchanged) — see `useGlobalMapUrlSync.ts`'s own doc comment on its
  // `pageSizeFromUrl` state for what this real presence signal fixes that a
  // plain `pageSize !== LIST_PAGE_SIZE` comparison could not.
  pageSizeFromUrl: number | null = null
): UseListFeaturesResult {
  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectActiveFilters)
  const searchTerm = useAppSelector(selectSearchTerm)
  const activeLayers = useAppSelector(selectActiveLayers)
  const listEntity = useAppSelector(selectListEntity)
  const sort = useAppSelector(selectListSort)
  const direction = useAppSelector(selectListDirection)
  const listState = useAppSelector(selectListState)
  const { data, status, currentPage, pageSize } = listState
  const persistedPageSize = useAppSelector((state) =>
    selectTableTabPageSize(state, LIST_PREFS_TABLE_KEY, LIST_PREFS_TAB_KEY)
  )

  const hasFetchedInitial = useRef(false)
  const lastFetchedFiltersRef = useRef<GlobalMapFilters | null>(null)
  const lastFetchedLayersRef = useRef<EntityType[] | null>(null)
  const lastFetchedListEntityRef = useRef<ListEntityTab | null>(null)
  const lastFetchedSortRef = useRef<ListSortField | null>(null)
  const lastFetchedDirectionRef = useRef<"asc" | "desc" | null>(null)
  // Seeded from `searchTerm`'s own initial value (always `""`, same as
  // `useMapFeatures.ts`'s identical ref) so the first run is skipped for
  // free, the same way the other refs are seeded by the mount effect itself.
  const lastFetchedSearchRef = useRef(searchTerm)
  const lastFetchedPageRef = useRef(currentPage)
  const lastFetchedPageSizeRef = useRef(pageSize)

  useEffect(() => {
    if (!ready || hasFetchedInitial.current) return
    hasFetchedInitial.current = true

    // pageSize precedence, layered (fix, review round — the previous single
    // `pageSize !== LIST_PAGE_SIZE` check couldn't tell "no URL override"
    // apart from "the URL explicitly set `?pageSize=10`, which happens to
    // equal the default", so an explicit `?pageSize=10` deep link could get
    // silently overridden by a differing persisted preference):
    //
    // (A) `pageSize` already differs from `LIST_PAGE_SIZE` — something has
    //     already resolved this THIS SESSION (a previous mount's own
    //     resolution, or a user's live footer choice) and it survived in
    //     Redux across this mount (nothing resets it — see this file's own
    //     top-level doc comment). Trust it as-is; this is what makes a
    //     second List-tab visit keep a manually-chosen size instead of
    //     reverting to a stale `pageSizeFromUrl` frozen from the very first
    //     mount.
    // (B) `pageSize` still equals the default AND the URL explicitly named
    //     it (`pageSizeFromUrl !== null`, the real presence signal
    //     `useGlobalMapUrlSync` provides) — the URL wins over a differing
    //     persisted preference. This is the case (A) alone cannot resolve
    //     unambiguously, and is the fix this round.
    // (C) Neither — genuinely untouched. Falls back to the user's persisted
    //     preference (validated against the 5/10/20/50 set), then
    //     `LIST_PAGE_SIZE`.
    const initialPageSize =
      pageSize !== LIST_PAGE_SIZE
        ? pageSize
        : pageSizeFromUrl !== null
          ? pageSizeFromUrl
          : (asValidPageSize(persistedPageSize) ?? LIST_PAGE_SIZE)

    lastFetchedFiltersRef.current = filters
    lastFetchedLayersRef.current = activeLayers
    lastFetchedListEntityRef.current = listEntity
    lastFetchedSortRef.current = sort
    lastFetchedDirectionRef.current = direction
    lastFetchedPageRef.current = currentPage
    lastFetchedPageSizeRef.current = initialPageSize

    // Only dispatched when it actually changes `pageSize` in the store — the
    // six triggers below (and the two page/pageSize ones) all read `pageSize`
    // back out of the store for their own fetches, so the persisted-
    // preference-resolved value has to land there, not just in this one
    // fetch call.
    if (initialPageSize !== pageSize) dispatch(setListPageSize(initialPageSize))

    dispatch(
      fetchListRows({
        filters,
        search: searchTerm,
        entities: activeLayers,
        listEntity,
        sort,
        direction,
        page: currentPage,
        pageSize: initialPageSize
      })
    )
  }, [ready, dispatch])

  // Refetches whenever the committed filters change — same object-identity
  // dependency `useMapFeatures.ts`'s own filters effect uses (`setFilters`/
  // `clearFilters` are the only actions that reassign `committedQuery.filters`).
  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (filters === lastFetchedFiltersRef.current) return
    lastFetchedFiltersRef.current = filters
    lastFetchedPageRef.current = 1

    dispatch(setListPage(1))
    dispatch(
      fetchListRows({
        filters,
        search: searchTerm,
        entities: activeLayers,
        listEntity,
        sort,
        direction,
        page: 1,
        pageSize
      })
    )
  }, [filters, dispatch])

  // Debounced the same way `useMapFeatures.ts`'s own search effect is —
  // every keystroke reaches this hook directly via `setSearchTerm`, and the
  // cleanup from the previous run cancels its pending timeout before a new
  // one is armed, so only the last keystroke in a burst ever fetches.
  useEffect(() => {
    if (searchTerm === lastFetchedSearchRef.current) return
    lastFetchedSearchRef.current = searchTerm

    const handle = setTimeout(() => {
      lastFetchedPageRef.current = 1
      dispatch(setListPage(1))
      dispatch(
        fetchListRows({
          filters,
          search: searchTerm,
          entities: activeLayers,
          listEntity,
          sort,
          direction,
          page: 1,
          pageSize
        })
      )
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(handle)
  }, [searchTerm, dispatch])

  // Refetches whenever the "Projects & Leads" layer toggle changes — mirrors
  // `useMapFeatures.ts`'s own layer effect; `fetchListRows`'s `condition`
  // refuses to fire at all once every layer is off, same guard
  // `fetchMapFeatures` already has for the identical (backend 400s on an
  // empty `entities`) reason.
  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (activeLayers === lastFetchedLayersRef.current) return
    lastFetchedLayersRef.current = activeLayers
    lastFetchedPageRef.current = 1

    dispatch(setListPage(1))
    dispatch(
      fetchListRows({
        filters,
        search: searchTerm,
        entities: activeLayers,
        listEntity,
        sort,
        direction,
        page: 1,
        pageSize
      })
    )
  }, [activeLayers, dispatch])

  // Refetches on a sub-tab change. `setListEntity` already resets
  // `state.list.currentPage` to 1 itself (`global-map.slice.ts`), so by the
  // time this effect runs `currentPage` is already 1 — only this hook's own
  // `lastFetchedPageRef` needs to be kept in lockstep, so the page-trigger
  // effect below doesn't double-fetch.
  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (listEntity === lastFetchedListEntityRef.current) return
    lastFetchedListEntityRef.current = listEntity
    lastFetchedPageRef.current = 1

    dispatch(
      fetchListRows({
        filters,
        search: searchTerm,
        entities: activeLayers,
        listEntity,
        sort,
        direction,
        page: 1,
        pageSize
      })
    )
  }, [listEntity, dispatch])

  // Same as the sub-tab effect above: `setListSort` already resets
  // `currentPage` itself.
  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (sort === lastFetchedSortRef.current) return
    lastFetchedSortRef.current = sort
    lastFetchedPageRef.current = 1

    dispatch(
      fetchListRows({
        filters,
        search: searchTerm,
        entities: activeLayers,
        listEntity,
        sort,
        direction,
        page: 1,
        pageSize
      })
    )
  }, [sort, dispatch])

  // Same as the two effects above: `toggleListDirection` already resets
  // `currentPage` itself.
  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (direction === lastFetchedDirectionRef.current) return
    lastFetchedDirectionRef.current = direction
    lastFetchedPageRef.current = 1

    dispatch(
      fetchListRows({
        filters,
        search: searchTerm,
        entities: activeLayers,
        listEntity,
        sort,
        direction,
        page: 1,
        pageSize
      })
    )
  }, [direction, dispatch])

  // The 7th trigger: `currentPage` itself, driven by `setPage` below (this
  // hook's own return value, called directly or from `ListPaginationFooter`).
  // Every trigger effect above pre-seeds `lastFetchedPageRef.current = 1`
  // before its own page-1 fetch, so this effect sees no real change to act
  // on when one of those already handled the reset — see this file's own
  // doc comment for why that matters. Also pre-seeds `lastFetchedPageSizeRef`
  // to the CURRENT `pageSize` (unchanged here, but this fetch's own request
  // already carries it) so a page-size change landing in the same commit as
  // a page-1 reset (the 8th trigger below, via `ListPaginationFooter`'s
  // `onChange`) doesn't also fire from that other effect — see the doc
  // comment above for why both directions need this.
  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (currentPage === lastFetchedPageRef.current) return
    lastFetchedPageRef.current = currentPage
    lastFetchedPageSizeRef.current = pageSize

    dispatch(
      fetchListRows({
        filters,
        search: searchTerm,
        entities: activeLayers,
        listEntity,
        sort,
        direction,
        page: currentPage,
        pageSize
      })
    )
  }, [currentPage, dispatch])

  // The 8th trigger (Task 9): `pageSize` itself, driven by `setPageSize`
  // below. Symmetric with the 7th trigger above — pre-seeds
  // `lastFetchedPageRef` to the current `currentPage` for the identical
  // reason, so the two never both fire for one combined page+size change.
  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (pageSize === lastFetchedPageSizeRef.current) return
    lastFetchedPageSizeRef.current = pageSize
    lastFetchedPageRef.current = currentPage

    dispatch(
      fetchListRows({
        filters,
        search: searchTerm,
        entities: activeLayers,
        listEntity,
        sort,
        direction,
        page: currentPage,
        pageSize
      })
    )
  }, [pageSize, dispatch])

  const setPage = useCallback(
    (page: number) => {
      dispatch(setListPage(page))
    },
    [dispatch]
  )

  // Persists immediately (optimistic, rolled back on a failed PATCH by
  // `patchListPageSize` itself) — the same "hook dispatches straight into
  // both the live slice and the persisted preference" shape `setColumns` in
  // `use-table-column-config.tsx` already establishes for the analogous
  // `patchTablePrefs`. Resetting to page 1 is deliberately NOT this
  // function's job — `ListPaginationFooter`'s own `onChange` already calls
  // `onPageChange(1)` alongside this, per its own doc comment.
  const setPageSize = useCallback(
    (nextPageSize: number) => {
      dispatch(setListPageSize(nextPageSize))
      dispatch(
        patchListPageSize({
          tableKey: LIST_PREFS_TABLE_KEY,
          tabKey: LIST_PREFS_TAB_KEY,
          pageSize: nextPageSize
        })
      )
    },
    [dispatch]
  )

  return { data, status, currentPage, setPage, pageSize, setPageSize }
}
